import {
  useEffect,
  useState
} from "react";

import {
  formatMoney
} from "../../utils/formatMoney";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid
} from "recharts";

import {
  useNavigate
} from "react-router-dom";

import api from "../../api";

function RevenusList() {

  const [data, setData] = useState([]);

  const [chartData, setChartData] = useState([]);

  // ==========================
  // TOTAUX PAR DEVISE
  // ==========================

  const [totaux, setTotaux] = useState({
    USD: 0,
    CDF: 0
  });

  const [filtre, setFiltre] =
    useState("annee");

  const [preset, setPreset] =
    useState("");

  const [dateDebut, setDateDebut] =
    useState("");

  const [dateFin, setDateFin] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [sort, setSort] =
    useState("");

  const [page, setPage] =
    useState(1);

  const [totalPages, setTotalPages] =
    useState(1);

  const [error, setError] =
    useState("");

  const navigate =
    useNavigate();

  // =====================
  // FORMAT DATE
  // =====================

  const formatDate = (d) => {

    const date = new Date(d);

    if (isNaN(date)) {
      return "-";
    }

    return date.toLocaleDateString();
  };

  // =====================
  // FORMAT DEVISE
  // =====================

  const formatAmount = (
    montant,
    devise = "USD"
  ) => {

    return `${formatMoney(
      Number(montant || 0)
    )} ${devise}`;
  };

  // =====================
  // LIBELLE SOURCE
  // =====================

  const getSourceLabel = (ligne) => {

    if (
      ligne.source === "prestations" ||
      ligne.categorie === "blanchisserie"
    ) {
      return "Prestations";
    }

    return "Hébergement";
  };

  // =====================
  // LIBELLE CATEGORIE
  // =====================

  const getCategorieLabel = (ligne) => {

    if (ligne.categorie === "blanchisserie") {
      return "Blanchisserie";
    }

    if (ligne.categorie === "hebergement") {
      return "Hébergement";
    }

    return ligne.categorie || "-";
  };

  // =====================
  // NOM AFFICHÉ
  // =====================

  const getClientLabel = (ligne) => {

    if (
      ligne.nom ||
      ligne.prenom
    ) {
      return `${ligne.nom || ""} ${ligne.prenom || ""}`.trim();
    }

    if (ligne.designation) {
      return ligne.designation;
    }

    return "Client non renseigné";
  };

  // =====================
  // FETCH
  // =====================

  const fetchData = async () => {

    try {

      setError("");

      const res =
        await api.get(
          "/revenus",
          {
            params: {
              page,

              limit: 20,

              filtre:
                preset
                  ? ""
                  : filtre,

              preset,

              dateDebut,

              dateFin,

              sort
            }
          }
        );

      const payload =
        res.data;

      if (
        !payload ||
        !Array.isArray(
          payload.data
        )
      ) {

        throw new Error(
          "Format backend invalide"
        );
      }

      // ==========================
      // DATA
      // ==========================

      let safeData =
        payload.data;

      // ==========================
      // SEARCH
      // ==========================

      if (search) {

        const terme =
          search
            .toLowerCase()
            .trim();

        safeData =
          safeData.filter(
            (d) => {

              const texte = [
                d.nom,
                d.prenom,
                d.facture,
                d.appartement,
                d.reference_prestation,
                d.designation,
                d.categorie,
                d.source
              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();

              return texte.includes(
                terme
              );
            }
          );
      }

      // ==========================
      // GRAPHIQUE
      // ==========================
      //
      // IMPORTANT :
      // USD et CDF ne doivent jamais
      // être additionnés.
      //
      // On construit donc un point
      // par date avec deux séries.
      // ==========================

      const chartMap = {};

      if (
        Array.isArray(
          payload.chart
        )
      ) {

        payload.chart.forEach(
          (c) => {

            const date =
              c.date;

            if (
              !chartMap[date]
            ) {

              chartMap[date] = {
                date,
                USD: 0,
                CDF: 0
              };
            }

            const devise =
              String(
                c.devise || "USD"
              ).toUpperCase();

            const montant =
              Number(
                c.revenus || 0
              );

            if (
              devise === "CDF"
            ) {

              chartMap[date].CDF +=
                montant;

            } else {

              chartMap[date].USD +=
                montant;
            }

          }
        );
      }

      const safeChart =
        Object.values(
          chartMap
        ).map(
          (c) => ({
            ...c,
            date:
              new Date(
                c.date
              ).toLocaleDateString()
          })
        );

      // ==========================
      // TOTAUX
      // ==========================

      const backendTotaux =
        payload.totalMontant ||
        payload.totaux ||
        {};

      setTotaux({
        USD:
          Number(
            backendTotaux.USD || 0
          ),

        CDF:
          Number(
            backendTotaux.CDF || 0
          )
      });

      setData(
        safeData
      );

      setChartData(
        safeChart
      );

      setTotalPages(
        Number(
          payload.totalPages
        ) || 1
      );

    } catch (err) {

      console.error(
        "ERREUR REVENUS FRONTEND:",
        err
      );

      setError(
        "Erreur chargement revenus"
      );

      setData([]);

      setChartData([]);

      setTotaux({
        USD: 0,
        CDF: 0
      });
    }
  };

  useEffect(() => {

    fetchData();

  }, [
    page,
    filtre,
    preset,
    dateDebut,
    dateFin,
    sort,
    search
  ]);

  // =====================
  // RESET
  // =====================

  const reset = () => {

    setDateDebut("");

    setDateFin("");

    setPreset("");

    setFiltre(
      "annee"
    );

    setSearch("");

    setSort("");

    setPage(1);
  };

  // =====================
  // RENDER
  // =====================

  return (

    <div className="p-3 md:p-6 space-y-5 min-h-[100dvh] bg-gray-100">

      {/* =========================
          HEADER
      ========================== */}

      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">

        <h2 className="text-2xl md:text-3xl font-bold">
          Revenus
        </h2>

        <button
          onClick={reset}
          className="bg-gray-300 hover:bg-gray-400 transition px-4 py-3 rounded-xl"
        >
          Reset
        </button>

      </div>

      {/* =========================
          ERROR
      ========================== */}

      {error && (

        <div className="bg-red-100 border border-red-200 text-red-600 p-3 rounded-xl">
          {error}
        </div>

      )}

      {/* =========================
          SEARCH
      ========================== */}

      <input
        type="text"
        placeholder="Rechercher client, facture, prestation..."
        value={search}
        onChange={(e) => {

          setSearch(
            e.target.value
          );

          setPage(1);

        }}
        className="w-full border rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
      />

      {/* =========================
          TRI
      ========================== */}

      <div className="flex flex-wrap gap-2">

        <button
          onClick={() =>
            setSort(
              "montant"
            )
          }
          className="bg-green-600 hover:bg-green-700 transition text-white px-4 py-2 rounded-xl"
        >
          Trier montant
        </button>

        <button
          onClick={() =>
            setSort(
              "client"
            )
          }
          className="bg-blue-600 hover:bg-blue-700 transition text-white px-4 py-2 rounded-xl"
        >
          Trier client
        </button>

      </div>

      {/* =========================
          FILTRES
      ========================== */}

      <div className="flex flex-wrap gap-2">

        {[
          "jour",
          "semaine",
          "mois",
          "annee"
        ].map((f) => (

          <button
            key={f}
            onClick={() => {

              setFiltre(f);

              setPreset("");

              setPage(1);

            }}
            className={`px-4 py-2 rounded-xl transition ${
              filtre === f
                ? "bg-gray-900 text-white"
                : "bg-white"
            }`}
          >
            {f}
          </button>

        ))}

      </div>

      {/* =========================
          PRESETS
      ========================== */}

      <div className="flex flex-wrap gap-2">

        <button
          onClick={() => {

            setPreset(7);

            setFiltre("");

            setPage(1);

          }}
          className="bg-indigo-600 hover:bg-indigo-700 transition text-white px-4 py-2 rounded-xl"
        >
          7 jours
        </button>

        <button
          onClick={() => {

            setPreset(30);

            setFiltre("");

            setPage(1);

          }}
          className="bg-purple-600 hover:bg-purple-700 transition text-white px-4 py-2 rounded-xl"
        >
          30 jours
        </button>

      </div>

      {/* =========================
          DATES
      ========================== */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

        <input
          type="date"
          value={dateDebut}
          onChange={(e) => {

            setDateDebut(
              e.target.value
            );

            setPage(1);

          }}
          className="border rounded-xl px-4 py-3"
        />

        <input
          type="date"
          value={dateFin}
          onChange={(e) => {

            setDateFin(
              e.target.value
            );

            setPage(1);

          }}
          className="border rounded-xl px-4 py-3"
        />

      </div>

      {/* =========================
          TOTAUX
      ========================== */}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* USD */}

        <div className="bg-white rounded-2xl shadow p-5 border-l-4 border-green-500">

          <p className="text-sm text-gray-500">
            Total revenus USD
          </p>

          <p className="text-3xl font-bold text-green-600 mt-2">
            {formatAmount(
              totaux.USD,
              "USD"
            )}
          </p>

        </div>

        {/* CDF */}

        <div className="bg-white rounded-2xl shadow p-5 border-l-4 border-blue-500">

          <p className="text-sm text-gray-500">
            Total revenus CDF
          </p>

          <p className="text-3xl font-bold text-blue-600 mt-2">
            {formatAmount(
              totaux.CDF,
              "CDF"
            )}
          </p>

        </div>

      </div>

      {/* =========================
          GRAPHIQUE
      ========================== */}

      <div className="bg-white rounded-2xl shadow p-3 md:p-5">

        <h3 className="font-semibold text-lg mb-4">
          Évolution des revenus
        </h3>

        {chartData.length === 0 ? (

          <p className="text-gray-500">
            Aucune donnée graphique
          </p>

        ) : (

          <ResponsiveContainer
            width="100%"
            height={320}
          >

            <LineChart
              data={
                chartData
              }
            >

              <CartesianGrid
                strokeDasharray="3 3"
              />

              <XAxis
                dataKey="date"
              />

              <YAxis />

              <Tooltip
                formatter={(
                  value,
                  name
                ) => [
                  formatAmount(
                    value,
                    name
                  ),
                  name
                ]}
              />

              <Legend />

              <Line
                type="monotone"
                dataKey="USD"
                stroke="#16a34a"
                strokeWidth={3}
                name="USD"
              />

              <Line
                type="monotone"
                dataKey="CDF"
                stroke="#2563eb"
                strokeWidth={3}
                name="CDF"
              />

            </LineChart>

          </ResponsiveContainer>

        )}

      </div>

      {/* =========================
          MOBILE
      ========================== */}

      <div className="grid gap-3 md:hidden">

        {data.length === 0 && (

          <div className="bg-white rounded-2xl shadow p-6 text-center text-gray-500">
            Aucune donnée
          </div>

        )}

        {data.map((d) => (

          <div
            key={`${d.source}-${d.id}`}
            className="bg-white rounded-2xl shadow border p-4"
          >

            <div className="flex justify-between gap-3 items-start">

              <div>

                {/* CLIENT OU PRESTATION */}

                {d.client_id ? (

                  <p
                    onClick={() =>
                      navigate(
                        `/clients/${d.client_id}`
                      )
                    }
                    className="font-semibold text-blue-600 cursor-pointer"
                  >
                    {d.nom}{" "}
                    {d.prenom}
                  </p>

                ) : (

                  <p className="font-semibold text-gray-800">
                    {d.designation ||
                      "Prestation"}
                  </p>

                )}

                <p className="text-sm text-gray-500 mt-1">
                  {d.appartement ||
                    "-"}
                </p>

              </div>

              <div className="text-right">

                <p className="text-green-600 font-bold">

                  {formatAmount(
                    d.montant,
                    d.devise
                  )}

                </p>

                <p className="text-xs text-gray-500 mt-1">
                  {formatDate(
                    d.date_paiement
                  )}
                </p>

              </div>

            </div>

            {/* SOURCE / CATEGORIE */}

            <div className="mt-3 flex flex-wrap gap-2">

              <span className="text-xs bg-gray-100 px-2 py-1 rounded-lg">
                {getSourceLabel(d)}
              </span>

              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg">
                {getCategorieLabel(d)}
              </span>

            </div>

            {/* REFERENCE PRESTATION */}

            {d.reference_prestation && (

              <p className="text-xs text-gray-500 mt-3">
                Référence :{" "}
                <span className="font-medium">
                  {d.reference_prestation}
                </span>
              </p>

            )}

            {/* ACTIONS */}

            <div className="mt-4 grid grid-cols-2 gap-2">

              {d.facture_id ? (

                <button
                  onClick={() =>
                    navigate(
                      `/facture/${d.facture_id}`
                    )
                  }
                  className="bg-indigo-100 text-indigo-700 py-2 rounded-xl"
                >
                  Facture
                </button>

              ) : (

                <div />

              )}

              <button
                onClick={() =>
                  navigate(
                    `/recu/${d.id}`
                  )
                }
                className="bg-green-100 text-green-700 py-2 rounded-xl"
              >
                Reçu
              </button>

            </div>

            <div className="mt-3 text-xs text-gray-500">

              Mode :
              {" "}
              {d.mode_paiement ||
                "-"}

            </div>

          </div>

        ))}

      </div>

      {/* =========================
          DESKTOP
      ========================== */}

      <div className="hidden md:block bg-white rounded-2xl shadow overflow-x-auto">

        <table className="min-w-full text-sm">

          <thead className="bg-gray-900 text-white">

            <tr>

              <th className="px-4 py-3 text-left">
                Source
              </th>

              <th className="px-4 py-3 text-left">
                Client / Prestation
              </th>

              <th className="px-4 py-3 text-left">
                Appartement
              </th>

              <th className="px-4 py-3 text-left">
                Catégorie
              </th>

              <th className="px-4 py-3 text-left">
                Date
              </th>

              <th className="px-4 py-3 text-left">
                Montant
              </th>

              <th className="px-4 py-3 text-left">
                Référence
              </th>

              <th className="px-4 py-3 text-left">
                Facture
              </th>

              <th className="px-4 py-3 text-left">
                Reçu
              </th>

              <th className="px-4 py-3 text-left">
                Mode
              </th>

            </tr>

          </thead>

          <tbody>

            {data.length === 0 ? (

              <tr>

                <td
                  colSpan="10"
                  className="text-center py-6 text-gray-500"
                >
                  Aucune donnée
                </td>

              </tr>

            ) : (

              data.map((d) => (

                <tr
                  key={`${d.source}-${d.id}`}
                  className="border-t hover:bg-gray-50"
                >

                  {/* SOURCE */}

                  <td className="px-4 py-3">

                    <span className="font-medium">
                      {getSourceLabel(d)}
                    </span>

                  </td>

                  {/* CLIENT / PRESTATION */}

                  <td className="px-4 py-3">

                    {d.client_id ? (

                      <span
                        className="text-blue-600 cursor-pointer hover:underline"
                        onClick={() =>
                          navigate(
                            `/clients/${d.client_id}`
                          )
                        }
                      >
                        {d.nom}{" "}
                        {d.prenom}
                      </span>

                    ) : (

                      <span className="font-medium">
                        {d.designation ||
                          "Prestation"}
                      </span>

                    )}

                  </td>

                  {/* APPARTEMENT */}

                  <td className="px-4 py-3 text-purple-600">

                    {d.appartement ||
                      "-"}

                  </td>

                  {/* CATEGORIE */}

                  <td className="px-4 py-3">

                    <span className="bg-gray-100 px-2 py-1 rounded-lg">

                      {getCategorieLabel(d)}

                    </span>

                  </td>

                  {/* DATE */}

                  <td className="px-4 py-3">

                    {formatDate(
                      d.date_paiement
                    )}

                  </td>

                  {/* MONTANT */}

                  <td className="px-4 py-3 font-semibold text-green-600 whitespace-nowrap">

                    {formatAmount(
                      d.montant,
                      d.devise
                    )}

                  </td>

                  {/* REFERENCE */}

                  <td className="px-4 py-3">

                    {d.reference_prestation ||
                      "-"}

                  </td>

                  {/* FACTURE */}

                  <td className="px-4 py-3">

                    {d.facture_id ? (

                      <span
                        className="text-indigo-600 cursor-pointer hover:underline"
                        onClick={() =>
                          navigate(
                            `/facture/${d.facture_id}`
                          )
                        }
                      >
                        {d.facture ||
                          "Voir facture"}
                      </span>

                    ) : (

                      "-"

                    )}

                  </td>

                  {/* RECU */}

                  <td className="px-4 py-3">

                    <span
                      className="text-green-600 cursor-pointer hover:underline"
                      onClick={() =>
                        navigate(
                          `/recu/${d.id}`
                        )
                      }
                    >
                      Voir reçu
                    </span>

                  </td>

                  {/* MODE */}

                  <td className="px-4 py-3">

                    {d.mode_paiement ||
                      "-"}

                  </td>

                </tr>

              ))

            )}

          </tbody>

        </table>

      </div>

      {/* =========================
          PAGINATION
      ========================== */}

      <div className="flex justify-between items-center">

        <button
          onClick={() =>
            setPage((p) =>
              Math.max(
                p - 1,
                1
              )
            )
          }
          disabled={page <= 1}
          className="bg-gray-200 hover:bg-gray-300 transition px-4 py-2 rounded-xl disabled:opacity-50"
        >
          ← Précédent
        </button>

        <span className="text-sm font-medium">
          Page {page} /{" "}
          {totalPages}
        </span>

        <button
          onClick={() =>
            setPage((p) =>
              Math.min(
                p + 1,
                totalPages
              )
            )
          }
          disabled={
            page >= totalPages
          }
          className="bg-gray-200 hover:bg-gray-300 transition px-4 py-2 rounded-xl disabled:opacity-50"
        >
          Suivant →
        </button>

      </div>

    </div>
  );
}

export default RevenusList;